import { type FC, useCallback, useMemo, useState } from 'react'

import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import * as spriteIcons from 'assets/svg/sprite'
import DateRangeModal from 'components/UI/Modal/DateRange/DateRangeModal'
import { useModalContext } from 'context/modalContext'
import { getShortIsoDate } from 'lib/dayjs'
import { useUserProfile } from 'store'

import styles from './AnalyticsPeriodDropdown.module.scss'
import DropDownSelect from '../DropdownSelectNew/DropDownSelect'

interface IAnalyticsPeriodDropdown {
    selectedPeriodId: string
    onSelectPeriod: (categoryId: { start: string; end: string }) => void
}

export const getLastPeriodFn = (value: number, timeUnit: string) => () => {
    const current = dayjs()

    return {
        start: current?.subtract(value, timeUnit as any).toISOString(),
        end: current?.toISOString()
    }
}

export const selectedToPeriod = {
    today: () => {
        const current = dayjs()
        const start = current.startOf('day').toISOString()
        const end = current.toISOString()
        return { start, end }
    },
    7: getLastPeriodFn(6, 'day'), // if I put 7 it gets 8 dates...
    30: getLastPeriodFn(29, 'day'), // similar here...
    '12month': getLastPeriodFn(11, 'month')
    // 'this_period': getLastPeriodFn(15, 'day'), // todo: remove when redoing this feature
}

const AnalyticsPeriodDropdown: FC<IAnalyticsPeriodDropdown> = ({
    selectedPeriodId,
    onSelectPeriod
}) => {
    const { t } = useTranslation()
    const profile = useUserProfile()!
    const [search, setSearch] = useState('')
    const modalData = useModalContext()
    const [selectedPeriodValue, setSelectedPeriodValue] =
        useState<string>(selectedPeriodId)

    const periods = useMemo(
        () => [
            { value: 'today', label: t('today') },
            { value: '7', label: t('last7Days') },
            { value: '30', label: t('last30Days') },
            { value: '12month', label: t('last12Months') },
            { value: 'this_period', label: t('thisPeriod') },
            { value: 'custom_period', label: t('customDates') }
        ],
        [t]
    )

    const handlePeriodSelect = useCallback(
        (selectedValue: string) => {
            setSelectedPeriodValue(selectedValue)
            const getPeriodFn =
                selectedToPeriod[selectedValue as keyof typeof selectedToPeriod]
    
            if (getPeriodFn) {
                const { start, end } = getPeriodFn()
                const validStart = dayjs(start)
                const validEnd = dayjs(end)
                
                onSelectPeriod({
                    start: validStart.isValid() ? validStart.toISOString() : '',
                    end: validEnd.isValid() ? validEnd.toISOString() : validStart.toISOString(),
                })
                return
            }
    
            switch (selectedValue) {
                case 'this_period':
                    if (profile.payout_frequency === 'weekly') {
                        const start = getShortIsoDate(dayjs().isoWeekday(1))
                        const end = getShortIsoDate(dayjs())
                        onSelectPeriod({
                            start,
                            end: dayjs(end).isValid() ? end : start,
                        })
                    } else if (profile.payout_frequency === 'monthly') {
                        const start = getShortIsoDate(dayjs().date(1))
                        const end = getShortIsoDate(dayjs())
                        onSelectPeriod({
                            start,
                            end: dayjs(end).isValid() ? end : start,
                        })
                    } else {
                        const { start, end } = getLastPeriodFn(15, 'day')()
                        onSelectPeriod({
                            start,
                            end: dayjs(end).isValid() ? end : start,
                        })
                    }
                    break
                case 'custom_period':
                    modalData.addModal(
                        t('customDateRange'),
                        <DateRangeModal
                            confirmFn={({ start, end }) => {
                                const validStart = dayjs(start)
                                const validEnd = dayjs(end)
    
                                onSelectPeriod({
                                    start: validStart.isValid()
                                        ? getShortIsoDate(validStart)
                                        : '',
                                    end: validEnd.isValid()
                                        ? getShortIsoDate(validEnd)
                                        : getShortIsoDate(validStart),
                                })
                            }}
                        />
                    )
                    break
                default:
                    console.error('Unknown period option selected')
                    break
            }
        },
        [modalData, onSelectPeriod, profile.payout_frequency, t]
    )
    

    return (
        <DropDownSelect
            customClass={styles.analyticsPeriodDropdown}
            notSearchable // so it doesn't open keyboard and reduce viewport
            icon={
                <div className='analytics-dropdown-icon'>
                    <spriteIcons.IconCalendarLinesWhite />
                </div>
            }
            options={periods}
            placeholder={t('date')}
            search={search}
            setSearch={setSearch}
            selectedOption={selectedPeriodValue}
            setSelectedOption={handlePeriodSelect}
        />
    )
}

export default AnalyticsPeriodDropdown
